import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteBusinessCaseProformaInvoiceSettingMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {Nullish, composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['branch'] as const;

export function ProformaCreationOverview() {
  const navigate = useNavigate();
  const handleCreate = () => navigate(settingsRoutes.proformaCreationCreate);
  const handleEdit = (id: string) =>
    navigate(composePath(settingsRoutes.proformaCreationEdit, {params: {id}}));

  const [deleteProformaCreation] = useDeleteBusinessCaseProformaInvoiceSettingMutation();

  const handleDelete = (id: string, branchName: string | Nullish) =>
    openDeleteDialog({
      onConfirm: () =>
        deleteProformaCreation({id})
          .unwrap()
          .then(() => refreshDmsDatagrid('checkout-proforma-invoice-settings'))
          .catch(handleApiError),
      text: i18n.t('entity.accounting.labels.deleteDefinitionDeleteText', {
        name: branchName,
      }),
    });

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;
    const branchName = getColumnData<{label: string}>('branch')?.value?.label;

    if (isNil(id)) {
      showNotification.error();
      return;
    }
    match(actionKey)
      .with('EDIT', () => handleEdit(id))
      .with('DELETE', () => handleDelete(id, branchName))
      .otherwise(() => handleEdit(id));
  };

  const title = i18n.t('page.accountingSettings.labels.proformaCreation');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          actions: [
            {
              type: 'button',
              onClick: handleCreate,
              title: i18n.t('page.accounting.actions.addDefinition'),
            },
          ],
        }}
        description={i18n.t('page.accountingSettings.labels.proformaCreationDescription')}
        data-testid={testIds.settings.proformaCreationOverview('settings-template')}
      >
        <DmsDataGrid
          gridCode="checkout-proforma-invoice-settings"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.proformaCreationOverview('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
